import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import { createAudioPlaybackController } from '~/utils/audio-playback.mjs'
import { createPlaybackTracker } from '~/utils/playback-events.mjs'
import { createGrowthEventId } from '~/utils/growthEvents'
import { sendSongEvent, songRequest, type PlaybackContext } from '~/services/songPlayback'

export type RepeatMode = 'off' | 'all' | 'one'
export interface PlayOptions { queue?: PlayerTrack[]; userInitiated?: boolean }
export type PlayerPhase = 'idle' | 'loading' | 'playing' | 'paused' | 'error'
export interface PlayerTrack {
  id: string
  title?: string
  audio_url?: string
  image_url?: string
  display_name?: string
  cover_hue?: number
  lyrics?: string
  lyrics_text?: string
  show_lyrics?: boolean
  instrumental?: boolean
  make_instrumental?: boolean
  vocal?: string
  lyrics_timing_status?: string
  lyrics_timing?: { url: string; version?: number }
  creator?: { display_name: string; avatar?: { url?: string | null; initial: string; hue: number }; badges?: string[]; display_badge?: { badge_key: string; tier: number } }
  liked_by_me?: boolean
  like_count?: number
  play_count?: number
  remix_allowed?: boolean
  metadata?: { duration?: number; prompt?: string; tags?: string; has_vocal?: boolean; instrumental?: boolean; make_instrumental?: boolean }
}

/** Presentation state plus the legacy event adapter. PlayerDock owns the audio. */
export const usePlayerStore = defineStore('player', () => {
  const currentTrack = ref<PlayerTrack | null>(null)
  const queue = ref<PlayerTrack[]>([])
  const phase = ref<PlayerPhase>('idle')
  const currentTime = ref(0)
  const duration = ref(0)
  const volume = ref(0.7)
  const shuffle = ref(false)
  const repeat = ref<RepeatMode>('off')
  // Tracks played while shuffling, so Previous retraces them and each song plays once per pass.
  let shuffleHistory: string[] = []
  const saveModes = () => { try { localStorage.setItem('musicmint-player-modes', JSON.stringify({ shuffle: shuffle.value, repeat: repeat.value })) } catch { /* Storage is optional. */ } }
  const toggleShuffle = () => { shuffle.value = !shuffle.value; shuffleHistory = []; saveModes() }
  const cycleRepeat = () => { repeat.value = repeat.value === 'off' ? 'all' : repeat.value === 'all' ? 'one' : 'off'; saveModes() }
  const muted = ref(false)
  const visible = ref(false)
  const hasPlayedThisSession = ref(false)
  const liveHue = ref(350)
  // Page-entry color is presentation only; keep the remembered song hue intact.
  const pageHue = ref<number | undefined>()
  const shellHue = computed(() => pageHue.value ?? liveHue.value)
  const setPageHue = (hue?: number) => { pageHue.value = hue }
  let rememberedHue = false
  const setBrandHue = (hue?: number) => { if (!currentTrack.value && !rememberedHue && typeof hue === 'number' && Number.isFinite(hue)) liveHue.value = ((hue % 360) + 360) % 360 }
  const nowPlayingOpen = ref(false)
  const showLyrics = ref(false)
  const playbackError = ref(false)
  const playbackContext = ref<PlaybackContext>({ surface: 'player' })
  const playbackSessionId = ref('')
  const timingUpdates = ref<Record<string, PlayerTrack>>({})
  const likeUpdates = ref<Record<string, { liked: boolean; like_count: number }>>({})
  const refreshCounts = new Map<string, number>()
  const refreshReasons = new Set<string>()
  const openedSongs = new Set<string>()
  let pendingTimer: ReturnType<typeof setTimeout> | undefined
  let ended = false
  const refreshPendingTiming = async (track: PlayerTrack, reason: string) => {
    const current = timingUpdates.value[track.id] || track
    const key = `${track.id}:${reason}`
    if (typeof window === 'undefined' || current.lyrics_timing_status !== 'pending' || refreshReasons.has(key) || (refreshCounts.get(track.id) || 0) >= 3) return
    refreshReasons.add(key)
    refreshCounts.set(track.id, (refreshCounts.get(track.id) || 0) + 1)
    try {
      const data = await songRequest('music/v1/get', { id: track.id })
      if (timingUpdates.value[track.id]?.lyrics_timing_status === 'ok' && data.lyrics_timing_status !== 'ok') return
      timingUpdates.value[track.id] = data
      if (currentTrack.value?.id === track.id) currentTrack.value = { ...currentTrack.value, ...data, display_name: data.creator?.display_name || currentTrack.value.display_name }
    } catch { /* Pending remains pending; later specified triggers can retry. */ }
  }
  const openSong = (track: PlayerTrack) => {
    if (openedSongs.has(track.id)) void refreshPendingTiming(track, `reopen:${createGrowthEventId()}`)
    openedSongs.add(track.id)
  }
  const tracker = createPlaybackTracker({ emit: (type: string, session: any, metadata: Record<string, unknown>) => {
    void sendSongEvent(session.id, type, session.context, session.sessionId, metadata)
    if (type === 'play_start' && currentTrack.value) {
      const track = currentTrack.value
      clearTimeout(pendingTimer)
      pendingTimer = setTimeout(() => { void refreshPendingTiming(track, `30s:${session.sessionId}`) }, 30000)
    }
  } })
  const playing = computed(() => phase.value === 'playing')
  const loading = computed(() => phase.value === 'loading')
  const lyrics = computed(() => {
    const track = currentTrack.value
    if (!track || track.show_lyrics === false || track.instrumental || track.make_instrumental || track.vocal === 'instrumental' || track.metadata?.instrumental || track.metadata?.make_instrumental) return ''
    return track.lyrics || track.lyrics_text || track.metadata?.prompt || ''
  })
  let audio: HTMLAudioElement | null = null
  let controller: ReturnType<typeof createAudioPlaybackController> | null = null
  let closeTimer: ReturnType<typeof setTimeout> | undefined

  const notifyVisibility = () => {
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('music-player-visibility-change', { detail: { isVisible: visible.value } }))
  }
  const notifyPlaybackState = () => {
    if (!currentTrack.value || typeof window === 'undefined') return
    window.dispatchEvent(new CustomEvent('playing-state-changed', { detail: { id: currentTrack.value.id, isPlaying: playing.value, isLoading: loading.value } }))
  }
  const notifyStopped = () => {
    if (currentTrack.value) window.dispatchEvent(new CustomEvent('playing-state-changed', { detail: { id: currentTrack.value.id, isPlaying: false } }))
  }
  watch(visible, notifyVisibility)
  watch(shellHue, hue => {
    if (typeof document !== 'undefined') document.documentElement.style.setProperty('--live-h', String(hue))
  }, { flush: 'sync' })
  watch(phase, value => {
    if (value === 'loading' || value === 'playing') pageHue.value = undefined
  }, { flush: 'sync' })

  const requestPlayback = () => {
    playbackError.value = false
    if (currentTrack.value) controller?.play({ id: currentTrack.value.id, url: currentTrack.value.audio_url })
  }
  const play = (track: PlayerTrack, context: PlaybackContext = {}, options: PlayOptions | boolean = {}) => {
    const userInitiated = typeof options === 'boolean' ? options : options.userInitiated !== false
    if (!track?.id) return
    if (typeof options === 'object' && options.queue) {
      const seen = new Set<string>()
      queue.value = options.queue.filter(song => song?.id && !seen.has(song.id) && Boolean(seen.add(song.id)))
      shuffleHistory = []
    }
    const changed = currentTrack.value?.id !== track.id
    if (changed || ended || !playbackSessionId.value) {
      if (audio) tracker.tick(audio.currentTime, duration.value, audio.playbackRate)
      clearTimeout(pendingTimer)
      playbackContext.value = { ...context, surface: context.surface || 'player', requestId: context.requestId || createGrowthEventId() }
      playbackSessionId.value = createGrowthEventId()
      tracker.select({ id: track.id, context: { ...playbackContext.value }, sessionId: playbackSessionId.value }, userInitiated)
      ended = false
    }
    currentTrack.value = track.creator ? { ...track, display_name: track.creator.display_name } : track
    visible.value = true
    hasPlayedThisSession.value = true
    liveHue.value = typeof track.cover_hue === 'number' && Number.isFinite(track.cover_hue) ? track.cover_hue : liveHue.value
    rememberedHue = true
    try { localStorage.setItem('musicmint-last-hue', String(liveHue.value)) } catch { /* Storage is optional. */ }
    if (!queue.value.some(item => item.id === track.id)) queue.value.push(track)
    if (changed) {
      currentTime.value = 0
      const knownDuration = Number(track.metadata?.duration || 0)
      duration.value = Number.isFinite(knownDuration) && knownDuration > 0 ? knownDuration : 0
    }
    if (audio) audio.volume = volume.value
    requestPlayback()
  }
  const toggle = () => { if (playing.value || loading.value) controller?.pause(); else if (ended && currentTrack.value) play(currentTrack.value, playbackContext.value); else requestPlayback() }
  /** Next queue index; -1 when the queue is finished and should not wrap. */
  const nextIndex = (wrap: boolean) => {
    const currentId = currentTrack.value?.id
    if (shuffle.value) {
      const played = new Set([...shuffleHistory, currentId])
      let pool = queue.value.filter(track => !played.has(track.id))
      if (!pool.length && wrap) { shuffleHistory = []; pool = queue.value.filter(track => track.id !== currentId) }
      return pool.length ? queue.value.indexOf(pool[Math.floor(Math.random() * pool.length)]) : -1
    }
    const index = queue.value.findIndex(track => track.id === currentId)
    return index < queue.value.length - 1 ? index + 1 : wrap ? 0 : -1
  }
  const next = (userInitiated = true) => {
    if (queue.value.length <= 1) return
    // Explicit Next always moves on; auto-advance stops after the last song unless Repeat all is on.
    const index = nextIndex(userInitiated || repeat.value === 'all')
    if (index < 0) return
    if (shuffle.value && currentTrack.value) shuffleHistory.push(currentTrack.value.id)
    play(queue.value[index], { surface: 'player' }, userInitiated)
  }
  const prev = () => {
    if (currentTime.value > 3) { seek(0); return }
    if (queue.value.length <= 1) return
    const previous = shuffle.value ? queue.value.find(track => track.id === shuffleHistory[shuffleHistory.length - 1]) : undefined
    if (previous) { shuffleHistory.pop(); play(previous); return }
    const index = queue.value.findIndex(track => track.id === currentTrack.value?.id)
    play(queue.value[index > 0 ? index - 1 : queue.value.length - 1])
  }
  const seek = (seconds: number) => {
    if (audio) tracker.suspend(audio.currentTime, duration.value, audio.playbackRate)
    if (audio && Number.isFinite(seconds) && Number.isFinite(duration.value) && duration.value > 0) audio.currentTime = Math.max(0, Math.min(duration.value, seconds))
  }
  const setVolume = (value: number) => {
    if (!Number.isFinite(value)) return
    volume.value = Math.max(0, Math.min(1, value))
    if (volume.value > 0) muted.value = false
    if (audio) { audio.volume = volume.value; audio.muted = muted.value }
  }
  const toggleMute = () => { muted.value = !muted.value; if (audio) audio.muted = muted.value }
  const close = () => {
    controller?.pause()
    visible.value = false
    nowPlayingOpen.value = false
    notifyStopped()
    notifyVisibility()
    clearTimeout(closeTimer)
    closeTimer = setTimeout(notifyVisibility, 100)
  }
  const onTime = () => { if (audio) { tracker.tick(audio.currentTime, duration.value, audio.playbackRate); currentTime.value = audio.currentTime } }
  const onSeeking = () => { if (audio) tracker.suspend(audio.currentTime, duration.value, audio.playbackRate) }
  const onSeeked = () => { if (audio) { tracker.seek(audio.currentTime); if (phase.value === 'playing' && !audio.paused) tracker.start(audio.currentTime) } }
  const onMetadata = () => { if (audio && Number.isFinite(audio.duration) && audio.duration > 0) duration.value = audio.duration }
  const onEnded = () => {
    if (!visible.value) return
    onTime(); ended = true; clearTimeout(pendingTimer)
    if (currentTrack.value) void refreshPendingTiming(currentTrack.value, `end:${playbackSessionId.value}`)
    if (currentTrack.value && (repeat.value === 'one' || (repeat.value === 'all' && queue.value.length <= 1))) { controller?.pause(); play(currentTrack.value, playbackContext.value, false); return }
    controller?.pause(); notifyStopped(); if (queue.value.length > 1) next(false)
  }
  const onPlay = (event: Event) => { const detail = (event as CustomEvent).detail; if (detail?.id) play(detail, detail.playbackContext) }
  const onToggle = (event: Event) => {
    const detail = (event as CustomEvent).detail
    if (!currentTrack.value || !detail || (detail.id && detail.id !== currentTrack.value.id)) return
    if (detail.isPlaying) requestPlayback()
    else controller?.pause()
  }
  const onRequest = () => { notifyVisibility(); notifyPlaybackState() }
  const attach = (element: HTMLAudioElement) => {
    if (audio === element) return
    if (audio) detach()
    try {
      const modes = JSON.parse(localStorage.getItem('musicmint-player-modes') || 'null')
      if (typeof modes?.shuffle === 'boolean') shuffle.value = modes.shuffle
      if (['off', 'all', 'one'].includes(modes?.repeat)) repeat.value = modes.repeat
    } catch { /* Storage is optional. */ }
    try { const stored = localStorage.getItem('musicmint-last-hue'); if (stored !== null && Number.isFinite(Number(stored))) { liveHue.value = ((Number(stored) % 360) + 360) % 360; rememberedHue = true } } catch { /* Storage is optional. */ }
    audio = element
    audio.volume = volume.value
    audio.muted = muted.value
    controller = createAudioPlaybackController(audio, {
      onState: (state: { phase: PlayerPhase }) => {
        if (audio) {
          if (state.phase === 'playing') tracker.start(audio.currentTime)
          else tracker.suspend(audio.currentTime, duration.value, audio.playbackRate)
        }
        phase.value = state.phase
        notifyPlaybackState()
        // Legacy senders set optimistic flags after dispatching their intent.
        queueMicrotask(() => { if (visible.value) notifyPlaybackState() })
      },
      onError: (error?: Error) => {
        if (error?.name === 'AbortError') return
        if (error?.name === 'NotAllowedError') {
          // Autoplay needs a user gesture. Keep the track ready for a tap.
          playbackError.value = false
          controller?.pause()
          return
        }
        playbackError.value = true
        notifyStopped()
      },
    })
    audio.addEventListener('timeupdate', onTime)
    audio.addEventListener('loadedmetadata', onMetadata)
    audio.addEventListener('ended', onEnded)
    audio.addEventListener('seeking', onSeeking)
    audio.addEventListener('seeked', onSeeked)
    window.addEventListener('play-music', onPlay)
    window.addEventListener('toggle-play-state', onToggle)
    window.addEventListener('request-music-player-state', onRequest)
    document.documentElement.style.setProperty('--live-h', String(shellHue.value))
    notifyVisibility()
  }
  const detach = () => {
    clearTimeout(closeTimer)
    clearTimeout(pendingTimer)
    tracker.reset()
    playbackSessionId.value = ''
    window.removeEventListener('play-music', onPlay)
    window.removeEventListener('toggle-play-state', onToggle)
    window.removeEventListener('request-music-player-state', onRequest)
    controller?.dispose()
    controller = null
    if (audio) {
      audio.removeEventListener('timeupdate', onTime)
      audio.removeEventListener('loadedmetadata', onMetadata)
      audio.removeEventListener('ended', onEnded)
      audio.removeEventListener('seeking', onSeeking)
      audio.removeEventListener('seeked', onSeeked)
      audio.pause()
      audio.removeAttribute('src')
      audio.load()
      audio = null
    }
    visible.value = false
    nowPlayingOpen.value = false
    phase.value = 'idle'
    currentTrack.value = null
    currentTime.value = duration.value = 0
    queue.value = []
    volume.value = 0.7
    muted.value = false
    playbackError.value = false
    // Keep the last song's atmosphere when the audio owner unmounts.
  }
  return { shuffle, repeat, toggleShuffle, cycleRepeat, currentTrack, queue, phase, currentTime, duration, volume, muted, visible, hasPlayedThisSession, liveHue, nowPlayingOpen, showLyrics, playbackError, playing, loading, lyrics,
    playbackContext, playbackSessionId, timingUpdates, likeUpdates, openSong, refreshPendingTiming,
    shellHue, setPageHue, setBrandHue, play, toggle, next, prev, seek, setVolume, toggleMute, close, attach, detach }
})
